
import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Button from '../base/Button';

interface HeaderProps {
  darkMode: boolean;
  setDarkMode: (value: boolean) => void;
}

export default function Header({ darkMode, setDarkMode }: HeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { name: 'Beranda', href: '/', icon: 'ri-home-line' },
    { name: 'Menu', href: '#menu', icon: 'ri-restaurant-line' },
    { name: 'Tentang', href: '#about', icon: 'ri-information-line' },
    { name: 'Reservasi', href: '#reservation', icon: 'ri-calendar-line' },
    { name: 'Galeri', href: '#gallery', icon: 'ri-image-line' },
    { name: 'Kontak', href: '#contact', icon: 'ri-phone-line' },
  ];

  const scrollToSection = (sectionId: string) => {
    if (sectionId.startsWith('#')) {
      // Jika sedang di halaman home, scroll ke section
      if (location.pathname === '/') {
        const element = document.querySelector(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        // Jika di halaman lain, navigasi ke home dulu kemudian scroll
        navigate('/');
        setTimeout(() => {
          const element = document.querySelector(sectionId);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 500);
      }
    }
  };

  const handleNavClick = (item: typeof navItems[0]) => {
    if (item.href === '/') {
      navigate('/');
    } else if (item.href.startsWith('#')) {
      scrollToSection(item.href);
    }
    setIsMenuOpen(false);
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-black/90 via-red-900/90 to-black/90 backdrop-blur-lg border-b border-red-500/20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-yellow-500 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                <span className="text-white font-black text-xl" style={{ fontFamily: '"Noto Sans JP", serif' }}>
                  竹
                </span>
              </div>
              <div>
                <h1
                  className="text-2xl font-black text-white group-hover:text-red-400 transition-colors"
                  style={{ fontFamily: '"Noto Sans JP", serif' }}
                >
                  TAKEYA
                  <span className="text-yellow-400 ml-2 text-lg">(竹や)</span>
                </h1>
                <p className="text-xs text-gray-300 font-medium" style={{ fontFamily: '"Orbitron", sans-serif' }}>
                  AUTHENTIC JAPANESE
                </p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              {navItems.map((item) => (
                <button
                  key={item.name}
                  onClick={() => handleNavClick(item)}
                  className="flex items-center space-x-2 text-gray-300 hover:text-red-400 font-medium transition-colors duration-300 cursor-pointer"
                >
                  <i className={`${item.icon} w-4 h-4 flex items-center justify-center`}></i>
                  <span>{item.name}</span>
                </button>
              ))}
            </nav>

            {/* Right Actions */}
            <div className="flex items-center space-x-4">
              {/* Dark/Light Mode Toggle */}
              <button
                onClick={() => setDarkMode(!darkMode)}
                className="w-10 h-10 bg-gray-800 hover:bg-gray-700 text-yellow-400 rounded-full flex items-center justify-center transition-colors cursor-pointer"
              >
                <i className={`${darkMode ? 'ri-sun-line' : 'ri-moon-line'} w-5 h-5 flex items-center justify-center`}></i>
              </button>

              {/* Admin Access */}
              <div className="hidden md:flex items-center space-x-2">
                <Link to="/customer">
                  <Button variant="outline" size="sm" className="whitespace-nowrap cursor-pointer">
                    <i className="ri-user-line mr-2 w-4 h-4 flex items-center justify-center"></i>
                    Pesan
                  </Button>
                </Link>
                <Link to="/login">
                  <Button variant="primary" size="sm" className="whitespace-nowrap cursor-pointer">
                    <i className="ri-settings-line mr-2 w-4 h-4 flex items-center justify-center"></i>
                    Panel
                  </Button>
                </Link>
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="lg:hidden w-10 h-10 bg-red-600 hover:bg-red-700 text-white rounded-full flex items-center justify-center transition-colors cursor-pointer"
              >
                <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'} w-5 h-5 flex items-center justify-center`}></i>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden bg-black/95 backdrop-blur-lg border-t border-red-500/20">
            <div className="max-w-7xl mx-auto px-6 py-6">
              <nav className="space-y-4">
                {navItems.map((item) => (
                  <button
                    key={item.name}
                    onClick={() => handleNavClick(item)}
                    className="flex items-center space-x-3 text-gray-300 hover:text-red-400 font-medium transition-colors w-full text-left cursor-pointer"
                  >
                    <i className={`${item.icon} w-5 h-5 flex items-center justify-center`}></i>
                    <span>{item.name}</span>
                  </button>
                ))}
                <div className="border-t border-gray-700 pt-4 space-y-2">
                  <Link to="/customer" className="block">
                    <Button variant="outline" size="sm" className="w-full whitespace-nowrap cursor-pointer">
                      <i className="ri-user-line mr-2 w-4 h-4 flex items-center justify-center"></i>
                      Pesan Makanan
                    </Button>
                  </Link>
                  <Link to="/login" className="block">
                    <Button variant="primary" size="sm" className="w-full whitespace-nowrap cursor-pointer">
                      <i className="ri-settings-line mr-2 w-4 h-4 flex items-center justify-center"></i>
                      Panel Admin
                    </Button>
                  </Link>
                </div>
              </nav>
            </div>
          </div>
        )}
      </header>

      {/* Spacer for fixed header */}
      <div className="h-20"></div>
    </>
  );
}
